import React, {useState, useEffect, useRef} from 'react'
import {useNavigate} from 'react-router-dom'
import {PageTitle} from '../../../_metronic/layout/core'
import {UsuariosTable} from './components/UsuariosTable'
import {Usuario} from './core/models'
import {fetchWithAuth} from '../../core/utils/fetchWithAuth'

interface FiltrosUsuarios {
  q?: string
  activo?: number
  es_admin?: number
}

const UsuariosPage: React.FC = () => {
  const navigate = useNavigate()
  
  // Estados para los datos y la carga
  const [usuarios, setUsuarios] = useState<Usuario[]>([] as Usuario[])
  const [loading, setLoading] = useState<boolean>(true)
  const [error, setError] = useState<string | null>(null)
  
  // Estados para la paginación del backend
  const [currentPage, setCurrentPage] = useState<number>(1)
  const [pageSize] = useState<number>(20)
  const [total, setTotal] = useState<number>(0)
  
  // Estados para los filtros
  const [filtros, setFiltros] = useState<FiltrosUsuarios>({})

  // Calcular totalPages basado en total y pageSize
  const totalPages = Math.ceil(total / pageSize)

  // Función para obtener el token del localStorage
  const getToken = (): string | null => {
    return localStorage.getItem('X-TOKEN')
  }

  // Función para obtener los usuarios del backend
  const fetchUsuarios = async (page: number = 1, filtrosData: FiltrosUsuarios = {}) => {
    try {
      setLoading(true)
      setError(null)
      
      const token = getToken()
      if (!token) {
        throw new Error('No se encontró el token de autenticación')
      }

      // Construir el objeto de filtros solo con los valores definidos
      const filtrosPayload: FiltrosUsuarios = {}
      if (filtrosData.q && filtrosData.q.trim() !== '') {
        filtrosPayload.q = filtrosData.q.trim()
      }
      if (filtrosData.activo !== undefined && filtrosData.activo !== null) {
        filtrosPayload.activo = filtrosData.activo
      }
      if (filtrosData.es_admin !== undefined && filtrosData.es_admin !== null) {
        filtrosPayload.es_admin = filtrosData.es_admin
      }

      const response = await fetchWithAuth(`${process.env.REACT_APP_API_URL}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-TOKEN': token
        },
        body: JSON.stringify({
          service: 'listUsuarios',
          page: page,
          pageSize: pageSize,
          filtros: filtrosPayload
        })
      })

      if (!response.ok) {
        throw new Error(`Error en la petición: ${response.status}`)
      }

      const data = await response.json()
      
      if (data.code === 'OK') {
        // Asegurar que siempre sea un array
        let usuariosArray: Usuario[] = []
        
        // Intentar diferentes estructuras de respuesta
        if (Array.isArray(data.data)) {
          usuariosArray = data.data
        } else if (data.data?.usuarios && Array.isArray(data.data.usuarios)) {
          usuariosArray = data.data.usuarios
        } else if (data.data?.data && Array.isArray(data.data.data)) {
          usuariosArray = data.data.data
        } else if (data.usuarios && Array.isArray(data.usuarios)) {
          usuariosArray = data.usuarios
        } else if (data.data && typeof data.data === 'object' && !Array.isArray(data.data)) {
          // Si data.data es un objeto, intentar extraer usuarios de diferentes formas
          if (data.data.list && Array.isArray(data.data.list)) {
            usuariosArray = data.data.list
          } else if (data.data.items && Array.isArray(data.data.items)) {
            usuariosArray = data.data.items
          }
        }
        
        // Si aún no es un array, usar array vacío
        if (!Array.isArray(usuariosArray)) {
          console.warn('La respuesta de la API no contiene un array de usuarios:', data)
          usuariosArray = []
        }
        
        setUsuarios(usuariosArray)
        setCurrentPage(data.data?.page || data.page || page)
        setTotal(data.data?.total || data.data?.count || data.total || data.count || usuariosArray.length || 0)
      } else if (data.code === 'ERROR' && data.returned_message && data.returned_message.includes('JWT expired')) {
        // Token expirado, redirigir al login
        localStorage.removeItem('X-TOKEN')
        navigate('/auth/login')
        return
      } else {
        throw new Error(data.returned_message || 'Error en la respuesta del servidor')
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error desconocido')
    } finally {
      setLoading(false)
    }
  }

  // Ref para rastrear si es la primera carga
  const isFirstLoad = useRef(true)
  const lastFiltrosRef = useRef<string>('')
  const lastPageRef = useRef<number>(1)

  // Cargar los datos al montar el componente o cuando cambien los filtros o la página
  useEffect(() => {
    const filtrosKey = JSON.stringify(filtros)
    const filtrosChanged = lastFiltrosRef.current !== filtrosKey
    const pageChanged = lastPageRef.current !== currentPage

    // Si es la primera carga, cargar datos
    if (isFirstLoad.current) {
      isFirstLoad.current = false
      fetchUsuarios(1, filtros)
      lastFiltrosRef.current = filtrosKey
      lastPageRef.current = 1
      return
    }

    // Si cambiaron los filtros, resetear a página 1 y cargar
    if (filtrosChanged) {
      setCurrentPage(1)
      fetchUsuarios(1, filtros)
      lastFiltrosRef.current = filtrosKey
      lastPageRef.current = 1
      return
    }

    // Si solo cambió la página, cargar esa página
    if (pageChanged && !filtrosChanged) {
      fetchUsuarios(currentPage, filtros)
      lastPageRef.current = currentPage
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtros, currentPage])

  // Función para cambiar de página
  const handlePageChange = (page: number) => {
    setCurrentPage(page)
  }

  // Función para refrescar la lista después de acciones
  const handleRefresh = () => {
    fetchUsuarios(currentPage, filtros)
  }

  // Función para actualizar filtros
  const handleFiltrosChange = (nuevosFiltros: FiltrosUsuarios) => {
    setFiltros(nuevosFiltros)
  }

  return (
    <>
      <PageTitle>Gestión de Usuarios</PageTitle>
      <div className='row g-5 g-xl-8'>
        <div className='col-xl-12'>
          <div className='card'>
            <div className='card-body'>
              {loading && (
                <div className='py-3'>
                  <div className='table-responsive'>
                    <table className='table'>
                      <thead>
                        <tr className='sticky-head'><th colSpan={7}>&nbsp;</th></tr>
                      </thead>
                      <tbody>
                        {Array.from({length: 6}).map((_, i) => (
                          <tr key={`sk-${i}`}>
                            {Array.from({length: 7}).map((__, j) => (
                              <td key={`skc-${i}-${j}`}>
                                <div className='placeholder-glow'><span className='placeholder col-8'></span></div>
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {error && (
                <div className='alert alert-danger' role='alert'>
                  {error}
                  <button 
                    className='btn btn-sm btn-outline-primary ms-2'
                    onClick={() => fetchUsuarios(currentPage)}
                  >
                    Reintentar
                  </button>
                </div>
              )}

              {!loading && !error && (
                <>
                  <UsuariosTable 
                    usuarios={usuarios} 
                    onRefresh={handleRefresh}
                    filtros={filtros}
                    onFiltrosChange={handleFiltrosChange}
                  />
                  
                  {/* Paginación del backend */}
                  {totalPages > 1 && (
                    <div className='d-flex flex-stack flex-wrap pt-6'>
                      <div className='fs-6 fw-semibold text-gray-700'>
                        Página {currentPage} de {totalPages} - Total: {total} usuarios
                      </div>
                      <ul className='pagination'>
                        <li className={`page-item previous ${currentPage === 1 ? 'disabled' : ''}`}>
                          <button
                            className='page-link'
                            onClick={() => handlePageChange(currentPage - 1)}
                            disabled={currentPage === 1}
                          >
                            <i className='previous'></i>
                          </button>
                        </li>
                        {[...Array(totalPages)].map((_, idx) => {
                          const pageNum = idx + 1
                          // Mostrar solo páginas cercanas a la actual
                          if (
                            pageNum === 1 ||
                            pageNum === totalPages ||
                            (pageNum >= currentPage - 2 && pageNum <= currentPage + 2)
                          ) {
                            return (
                              <li key={pageNum} className={`page-item ${currentPage === pageNum ? 'active' : ''}`}>
                                <button
                                  className='page-link'
                                  onClick={() => handlePageChange(pageNum)}
                                >
                                  {pageNum}
                                </button>
                              </li>
                            )
                          } else if (pageNum === currentPage - 3 || pageNum === currentPage + 3) {
                            return <li key={pageNum} className='page-item disabled'><span className='page-link'>...</span></li>
                          }
                          return null
                        })}
                        <li className={`page-item next ${currentPage === totalPages ? 'disabled' : ''}`}>
                          <button
                            className='page-link'
                            onClick={() => handlePageChange(currentPage + 1)}
                            disabled={currentPage === totalPages}
                          >
                            <i className='next'></i>
                          </button>
                        </li>
                      </ul>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

export default UsuariosPage

